import React from 'react';
import { useSelector } from 'hooks';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFieldArray } from 'react-final-form-arrays';
import { FormRenderProps, useField } from 'react-final-form';

import PageBaseProps from 'types/PageBase';
import { DocumentStatus, MobilityDocument, DocumentValidationType } from 'modules/selfServices/types/SelfService';

import { b } from 'utils/i18nUtils';
import { required } from 'utils/formUtils';

import Card from 'components/ui/Card';
import PageFooter from 'components/ui/PageFooter';
import { ExclamationTriangleIcon as WarningIcon } from '@heroicons/react/24/solid';
import { ExternalServiceStatus, UPDATE_FIXED_CACHE_KEY } from 'modules/selfServices/constants';
import { useGetAutomatedValidationStatusQuery, useUpdateSelfServiceMutation } from 'modules/selfServices/service';

import { getMobilityInstructions, isMobilityAccepted, isMobilityAnswered, shouldShowMobilityDocuments } from 'modules/selfServices/selectors';
import { isPxlCheckEligible } from 'modules/selfServices/utils';
import { booleanToYesNoChoice, YesNoChoice, yesNoChoiceToBoolean } from 'types/Question';

import Document from './Document';
import AutomatedDocumentValidator from './AutomatedDocumentValidator';
import YesNoCardActions from '../ui/Card/YesNoCardActions';
import MobilityInstructionsSection from './MobilityInstructions';
import { getSelectedSelfServiceId } from 'modules/auth/selectors';

const MobilityDocumentFormRender: React.FC<FormRenderProps & PageBaseProps> = ({
  isPenultimate,
  handleSubmit,
  errors = {},
  onPrev,
  shouldDisplayBackButton
}) => {
  const intl = useIntl();
  const selfServiceId = useSelector(getSelectedSelfServiceId);

  const [, { isLoading: isUpdateLoading }] = useUpdateSelfServiceMutation({ fixedCacheKey: UPDATE_FIXED_CACHE_KEY });
  const { data } = useGetAutomatedValidationStatusQuery(
    { selfServiceId: selfServiceId! },
    { skip: !selfServiceId },
  );
  const { status } = data ?? {};

  const isPxlDown = status === ExternalServiceStatus.HAS_ISSUES
    || status === ExternalServiceStatus.MAINTENANCE;

  const {
    input: { value: mobilityStatus, onChange: changeMobilityStatus },
    meta
  } = useField<boolean, HTMLElement, YesNoChoice>('customerAccepted', {
    validate: required,
    subscription: { value: true, error: true, submitFailed: true },
    format: booleanToYesNoChoice,
    parse: yesNoChoiceToBoolean,
  });

  const { fields: formDocuments } = useFieldArray<MobilityDocument>('documents', { subscription: { value: true } });
  const error = meta.submitFailed && meta.error && intl.formatMessage({ id: meta.error, defaultMessage: meta.error });

  const mobilityAlreadyAccepted = useSelector(isMobilityAccepted);
  const mobilityAlreadyAnswered = useSelector(isMobilityAnswered);
  const mobilityInstructions = useSelector(getMobilityInstructions);
  const canSeeMobilityDocuments = useSelector(shouldShowMobilityDocuments);

  const hasValidationErrors = Object.keys(errors).length > 0;

  React.useEffect(() => {
    if (isPxlDown) {
      formDocuments?.value?.forEach((doc, index) => {
        if (doc.status === DocumentStatus.REQUESTED) {
          formDocuments.update(index, { ...doc, status: DocumentStatus.SKIPPED });
        }
      });
    }
  // formDocuments unstable
  // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [isPxlDown]);

  const documents = React.useMemo(() => {
    const documentsFormNames = formDocuments.map((name) => name);
    const { value = [] } = formDocuments;
    return value.reduce((acc, { status }, index) => {
      if (isPxlCheckEligible(status)) {
        if (value[index].validationType === DocumentValidationType.MANUAL) {
          acc.requested.push(documentsFormNames[index]);
        }
        if (value[index].validationType === DocumentValidationType.AUTOMATIC) {
          acc.automated.push(documentsFormNames[index]);
        }
      } else if (status === DocumentStatus.REFUSED) {
        acc.refused.push(documentsFormNames[index]);
      }
      return acc;
    }, { requested: [], refused: [], automated: [] });
  }, [formDocuments]);

  const hasAcceptedMobility = mobilityStatus === YesNoChoice.YES;

  const hasRefusedDocuments = documents.refused.length > 0;
  const hasRequestedDocuments = documents.requested.length > 0;
  const showRefusedDocuments = hasRefusedDocuments && mobilityAlreadyAnswered && !isPxlDown;
  const showRequestedDocuments = hasRequestedDocuments
    && canSeeMobilityDocuments
    && (mobilityAlreadyAccepted || mobilityStatus === YesNoChoice.YES)
    && !isPxlDown;
  const hasAutomatedDocumentValidation = documents.automated.length > 0
    && (mobilityAlreadyAccepted || hasAcceptedMobility)
    && canSeeMobilityDocuments
    && !isPxlDown;
  const showInstructions = hasAcceptedMobility && (mobilityInstructions?.medias?.length > 0 || mobilityInstructions?.message);
  const showPxlMaintenanceMessage = isPxlDown && (mobilityAlreadyAccepted || hasAcceptedMobility);

  return (
    <>
      <main className="main-content max-w-content">
        <div className="content">
          {!mobilityAlreadyAnswered && (
            <>
              <Card
                mandatory
                centerText
                error={error}
                className="mb-5"
                title={intl.formatMessage({
                  id: 'mobilityDocument.question',
                  defaultMessage: 'Do you still want a courtesy vehicle?',
                })}
              >
                <YesNoCardActions onChange={changeMobilityStatus} value={mobilityStatus} />
              </Card>
              {showRequestedDocuments && (
                <>
                  <div className="h-px w-full bg-border-default mb-5 kiosk:mb-14" />
                  <p className="text-center">
                    <FormattedMessage
                      id="mobilityDocument.verify"
                      defaultMessage="In order to provide you with a courtesy vehicle, we need to <b>verify your information.</b>"
                      values={{ b }}
                    />
                  </p>
                </>
              )}
            </>
          )}
          {/* there will be only one automated, ever, as discussed with BE*/}
          {hasAutomatedDocumentValidation && <AutomatedDocumentValidator name={documents.automated[0]} />}
          {showPxlMaintenanceMessage && (
            <div className="warning-alert mt-xs rounded-card" data-testid="automated-validator-down">
              <div className="mr-xs">
                <WarningIcon className="size-l text-warning-default" />
              </div>
              <span className="text-xs!">
                <FormattedMessage
                  id="mobilityDocument.pxlMaintenance"

                  defaultMessage="Our external service provider is under maintenance at the moment. As soon as it is back on track, we will get in touch with you to verify your mobility documents."
                />
              </span>
            </div>

          )}
          {showRefusedDocuments && (
            <>
              <p className="text-center mb-3">
                <FormattedMessage
                  id="mobilityDocument.refused"
                  defaultMessage="The following documents have <b>been refused :</b>"
                  values={{ b }}
                />
              </p>
              <ul className="w-full rounded-card border border-border-default divide-y divide-gray-200">
                {documents.refused.map((name, index) =>
                  <li key={formDocuments.value[index].id}>
                    <Document name={name}/>
                  </li>
                )}
              </ul>
              <div className="h-px w-full bg-border-default kiosk:mb-14" />
            </>
          )}
          {showRequestedDocuments && (
            <>
              <p className="text-center">
                <FormattedMessage
                  id="mobilityDocument.takePicture"
                  defaultMessage="To complete the verification, please take pictures of the documents requested below."
                />
              </p>
              <ul className="w-full rounded-card border border-border-default divide-y divide-gray-200 mb-l">
                {documents.requested.map((name, index) =>
                  <li key={formDocuments.value[index].id}>
                    <Document name={name}/>
                  </li>
                )}
              </ul>
            </>
          )}
          {showInstructions && (<MobilityInstructionsSection mobilityInstructions={mobilityInstructions} />)}
        </div>
      </main>
      <PageFooter
        onPrev={onPrev}
        onNext={handleSubmit}
        loading={isUpdateLoading}
        isPenultimate={isPenultimate}
        disabledStyle={hasValidationErrors}
        shouldDisplayBackButton={shouldDisplayBackButton}
      />
    </>
  );
};

export default MobilityDocumentFormRender;
